import {
    logEvent as logAnalyticsEvent,
    setAnalyticsUserProperties,
} from './analytics';
import {
    clearRehydrationData,
    isValidUrl,
    isRehydratedMessage,
    openUrlForRedirect,
    setTextInputEnabled,
} from './helpers';
import {
    getConversationId,
    getCorrelationId,
    logEvent,
    updateCorrelationId,
} from './logging';
import { InputMode } from './hooks';
import { AmpFlagSet } from './amplitude';
import {
    Activity,
    Dispatch,
    GlobalInformationAlertType,
    GlobalInformationAlertValue,
    ScheduledRedirectValue,
} from './types';
import { Config } from './config';
import {
    MESSAGE_ASSIST_FULFILLMENT_TYPES,
    MESSAGE_ASSIST_MESSAGE_EVENT_TYPES,
} from './constants';
import { getFaqDataForIntent, postMessageAssistMessage } from './messageassist';
import getTypeSafeErrorMessage from './utils/getTypeSafeErrorMessage';

enum ActivityEventName {
    Subscenario = 'subscenario',
    ReplayUtterance = 'replayUtterance',
    AmplitudeEvent = 'amplitudeEvent',
    ShareLocation = 'shareLocation',
    SetInputMode = 'setInputMode',
    Login = 'login',
    Redirect = 'redirect',
    Inactive = 'inactive',
    CloseSession = 'closeSession',
    CloseChatBot = 'closeChatBot',
    ShowMessageSentConfirmation = 'showMessageSentConfirmation',
    ScheduledRedirect = 'scheduledRedirect',
}

type UserInfo = {
    firstName?: string;
    lastName?: string;
    age?: string | number;
    gender?: string;
    dob?: string;
};

type HandleEventActivityArgs = {
    activity: Activity;
    watermark: string;
    featureFlags: AmpFlagSet;
    correlationId: string;
    dispatch: Dispatch;
    config: Config;
    setInputMode: (mode: InputMode) => void;
    setInactive: (inactive: boolean) => void;
    sendUserLocation: (dispatch: Dispatch) => Promise<void>;
    setUserInfo: (info: UserInfo) => void;
    showGlobalInformationAlert: (
        type: GlobalInformationAlertType,
        alert: GlobalInformationAlertValue
    ) => void;
};

type HandleShowMessageSentConfirmationArgs = {
    activity: Activity;
    watermark: string;
    correlationId: string;
};

const handleShowMessageSentConfirmation = ({
    activity,
    watermark,
    correlationId,
}: HandleShowMessageSentConfirmationArgs): void => {
    const intent =
        activity.value && typeof activity.value.intent === 'string'
            ? activity.value.intent
            : undefined;
    const faqData = intent ? getFaqDataForIntent(intent) : undefined;

    // Notify Message Assist host app that the message was sent.
    void postMessageAssistMessage(
        MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.SHOW_SEND_CONFIRMATION,
        undefined,
        JSON.stringify({
            intent,
            showSurveyForCategory: faqData?.showSurveyForCategory || false,
            conversationId: getConversationId(),
            fulfillmentSource: faqData?.requiresConversation
                ? MESSAGE_ASSIST_FULFILLMENT_TYPES.FLOW
                : MESSAGE_ASSIST_FULFILLMENT_TYPES.QNA,
        })
    ).catch((error: unknown) => {
        logAnalyticsEvent('MessageAssistPostMessageFailed', {
            message_type:
                MESSAGE_ASSIST_MESSAGE_EVENT_TYPES.SHOW_SEND_CONFIRMATION,
            activity_name: activity.name,
            correlation_id: correlationId,
            watermark,
            intent,
            error_message: getTypeSafeErrorMessage(error),
        });
    });

    logAnalyticsEvent('ShowMessageSentConfirmation');
};

export const handleEventActivity = ({
    activity,
    watermark,
    featureFlags,
    correlationId,
    dispatch,
    config,
    setInputMode,
    setInactive,
    sendUserLocation,
    setUserInfo,
    showGlobalInformationAlert,
}: HandleEventActivityArgs): void => {
    // Always run user property / correlation updates for event activities
    const activityForAnalytics = activity as { name: string; value: string };
    setAnalyticsUserProperties(featureFlags, activityForAnalytics, watermark);
    updateCorrelationId(featureFlags, activityForAnalytics, watermark);

    switch (activity.name) {
        case ActivityEventName.Subscenario: {
            if (activity.value) {
                // Simulating user response. Delay it a little bit b/c it sometimes renders on UI before the bot message.
                setTimeout(() => {
                    dispatch({
                        type: 'WEB_CHAT/SEND_MESSAGE',
                        payload: {
                            text: activity.value,
                            textFormat: 'plain',
                            correlationId,
                        },
                    });
                }, 500);
            }
            break;
        }

        case ActivityEventName.ReplayUtterance: {
            if (
                !!activity.value &&
                !isRehydratedMessage(featureFlags, watermark)
            ) {
                // Replaying user utterance. Delay it a little bit b/c it sometimes renders on UI before the bot message.
                setTimeout(() => {
                    dispatch({
                        type: 'WEB_CHAT/SEND_MESSAGE',
                        payload: {
                            channelData: { postBack: true },
                            text: activity.value,
                            textFormat: 'plain',
                            correlationId,
                        },
                    });
                }, 500);
            }
            break;
        }

        case ActivityEventName.AmplitudeEvent: {
            if (!isRehydratedMessage(featureFlags, watermark)) {
                logAnalyticsEvent(
                    activity.value.eventType,
                    activity.value.eventProperties
                );
            }
            break;
        }

        case ActivityEventName.ShareLocation: {
            void sendUserLocation(dispatch);
            break;
        }

        case ActivityEventName.SetInputMode: {
            setInputMode(activity.value);
            setTextInputEnabled(featureFlags, config.tier, activity.value);
            break;
        }

        case ActivityEventName.Login: {
            logEvent({
                name: 'LoginActivityEventIgnored',
                properties: {
                    conversation_id: getConversationId(),
                    correlation_id: getCorrelationId(),
                },
            });
            break;
        }

        case ActivityEventName.Redirect: {
            if (!isRehydratedMessage(featureFlags, watermark)) {
                if (activity.value.amplitudeEvent) {
                    logAnalyticsEvent(
                        activity.value.amplitudeEvent.eventType,
                        activity.value.amplitudeEvent.eventProperties
                    );
                }
                openUrlForRedirect(
                    activity.value.url,
                    featureFlags,
                    config.mychartDomains
                );
            }
            break;
        }

        case ActivityEventName.Inactive: {
            setInactive(true);
            clearRehydrationData(config.tier);
            break;
        }

        case ActivityEventName.CloseSession: {
            clearRehydrationData(config.tier);
            break;
        }

        case ActivityEventName.CloseChatBot: {
            const parentWindow = window.parent.window;
            parentWindow.postMessage('closeChatbot', '*');
            break;
        }

        case ActivityEventName.ShowMessageSentConfirmation: {
            handleShowMessageSentConfirmation({
                activity,
                watermark,
                correlationId,
            });
            break;
        }

        case ActivityEventName.ScheduledRedirect: {
            const { target, delayMs, amplitudeEvent, redirectCallback } =
                activity.value as ScheduledRedirectValue;

            if (typeof target !== 'string' || !isValidUrl(target)) {
                logAnalyticsEvent(
                    'InvalidScheduledRedirectActivity, redirect will not happen',
                    {
                        conversationId: getConversationId(),
                        correlationId: getCorrelationId(),
                        activityValue: activity.value,
                    }
                );
                return;
            }

            showGlobalInformationAlert(
                GlobalInformationAlertType.ScheduledRedirectNotice,
                {
                    target,
                    delayMs,
                    amplitudeEvent,
                    redirectCallback,
                } as ScheduledRedirectValue
            );
            break;
        }
    }

    // Capture userInfo from Entrypoint scenario arguments
    if (
        activity.value?.scenario === 'Entrypoint' &&
        activity.value?.scenario_arguments
    ) {
        const { firstName, lastName, age, gender, dob } =
            activity.value.scenario_arguments;
        setUserInfo({ firstName, lastName, age, gender, dob });
    }
};
